import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FiBriefcase, FiMapPin } from 'react-icons/fi';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import WithBackground from '../Common/WithBackground';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './style.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace(
  '/backend/api',
  '/uploads',
);

const WorkerOffer = ({
  showTopOnly = false,
  headerTitle,
  headerDescription,
  bg,
  overlay,
}) => {
  const { t, i18n } = useTranslation();

  const getJobDetailLink = (jobId) => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/oferty-pracy',
      rus: '/rus/vakansii',
      ua: '/ua/vakansii',
      en: '/en/job-offers',
    };

    return `${langPaths[language] || langPaths.pl}/${jobId}`;
  };

  const getJobsListLink = () => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/oferty-pracy',
      rus: '/rus/vakansii',
      ua: '/ua/vakansii',
      en: '/en/job-offers',
    };

    return langPaths[language] || langPaths.pl;
  };

  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentLang = i18n.language || 'pl';
        const url = `${API_BASE_URL}/offers.php?lang=${currentLang}`;

        console.log('Fetching from URL:', url);
        console.log('showTopOnly:', showTopOnly);

        const response = await fetch(url);

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('API Response:', data);

        if (data.success && data.offers) {
          let filteredOffers = data.offers;

          if (showTopOnly) {
            filteredOffers = data.offers.filter((job) => job.is_top === true);
            console.log('Total offers received:', data.offers.length);
            console.log('Filtered top offers count:', filteredOffers.length);
            console.log('Top offers:', filteredOffers);
          }

          setJobsData(filteredOffers);
        } else {
          console.error('API returned unsuccessful response:', data);
          setError(data.error || 'Failed to load offers');
        }
      } catch (error) {
        console.error('Error loading offers:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadOffers();
  }, [i18n.language, showTopOnly]);

  const offerContent = (
    <section className='worker-offer'>
      <div className='worker-offer__container'>
        <div className='worker-offer__header'>
          <h2
            className='worker-offer__title'
            data-aos='fade-up'
            data-aos-delay='100'
          >
            {headerTitle || t('offers.title')}
          </h2>
          <p
            className='worker-offer__intro'
            data-aos='fade-up'
            data-aos-delay='200'
          >
            {headerDescription || t('offers.subtitle')}
          </p>
        </div>

        <div className='worker-offer__carousel'>
          {loading ? (
            <div className='worker-offer__loading'>
              <p>{t('common.loading') || 'Loading job offers...'}</p>
            </div>
          ) : error ? (
            <div className='worker-offer__error'>
              <p>
                {t('common.error') || 'Error loading offers'}: {error}
              </p>
            </div>
          ) : jobsData.length === 0 ? (
            <div className='worker-offer__empty'>
              <p>
                {t('offers.noOffersAvailable') ||
                  'No offers available at the moment'}
              </p>
            </div>
          ) : (
            <>
              <h3
                className='worker-offer__jobs-heading'
                data-aos='fade-up'
                data-aos-delay='300'
              >
                {t('workerOffer.topJobsWeekly')}
              </h3>

              <Swiper
                data-aos='fade-up'
                data-aos-delay='400'
                modules={[Navigation, Pagination]}
                spaceBetween={24}
                navigation={{
                  nextEl: '.worker-offer__next',
                  prevEl: '.worker-offer__prev',
                }}
                pagination={{
                  el: '.worker-offer__pagination',
                  clickable: true,
                }}
                breakpoints={{
                  0: {
                    slidesPerView: 1,
                    spaceBetween: 16,
                  },
                  769: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  1025: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                  },
                }}
                className='worker-offer__swiper'
              >
                {jobsData.map((job) => (
                  <SwiperSlide key={job.id}>
                    <div className='job-card'>
                      <div className='job-card__image'>
                        {job.is_top && (
                          <div className='job-card__top-badge'>
                            {t('workerOffer.topJob') || 'TOP'}
                          </div>
                        )}
                        <img
                          src={`${UPLOADS_BASE_URL}/${job.image}`}
                          alt={job.title}
                          onError={(e) => {
                            console.log('Image failed to load:', job.image);
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                        <div className='job-card__image-placeholder'>
                          <FiBriefcase />
                        </div>
                      </div>

                      <div className='job-card__content'>
                        <h3 className='job-card__title'>{job.title}</h3>

                        <div className='job-card__info'>
                          <div className='job-card__location'>
                            <FiMapPin className='job-card__location-icon' />
                            <span className='job-card__location-text'>
                              {job.location}
                            </span>
                          </div>

                          <div className='job-card__salary'>
                            <span className='job-card__salary-text'>
                              {job.description}
                            </span>
                          </div>
                        </div>

                        <Link
                          to={getJobDetailLink(job.id)}
                          className='job-card__button btn'
                        >
                          {t('common.readMore')}
                        </Link>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className='worker-offer__navigation'>
                <button className='worker-offer__prev' aria-label='Previous'>
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M15 18L9 12L15 6'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>

                <button className='worker-offer__next' aria-label='Next'>
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 18L15 12L9 6'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>

              <div className='worker-offer__pagination'></div>

              <div className='worker-offer__view-all'>
                <Link
                  to={getJobsListLink()}
                  className='worker-offer__view-all-button btn'
                >
                  {t('workerOffer.viewAllJobs')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {offerContent}
    </WithBackground>
  ) : (
    offerContent
  );
};

WorkerOffer.propTypes = {
  showTopOnly: PropTypes.bool,
  headerTitle: PropTypes.string,
  headerDescription: PropTypes.string,
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

WorkerOffer.defaultProps = {
  showTopOnly: false,
  headerTitle: null,
  headerDescription: null,
  bg: null,
  overlay: 'dark',
};

export default WorkerOffer;
